import {ColumnDefinition, ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {RowMetadata, TableColumnType} from 'notebook/tables/TableConstants';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {Optional} from 'util/Types';

import {ProcedureSearchItem} from './ProcedureSearchInput';
import {toProcedureSearchResponseFromTypedValue} from './ProcedureTypedValueUtils';

/**
 * RowMetadata to indicate that procedure ids referenced in a cell are read only.
 *
 * A cell editor may use this information to prevent removal of the id from the cell / provide a visual
 * affordance, but it is not guaranteed.
 */
export interface ProcedureColumnRowMetadata extends RowMetadata {
  procedureColumns: Array<{
    columnGroup: ColumnGroup;
    readOnlyIds: Array<string>;
  }>;
}

export type MaybeProcedureColumnRowMetadata = RowMetadata | ProcedureColumnRowMetadata | null;

/**
 * This function checks if a procedure is read only for a specific cell.
 *
 * This is currently only used by the ProcedureSearchInput component and TableColumnType.PROCEDURE,
 * however, if additional column types need similar "read only" functionality, this function can be
 * generalized and moved into a more general location.
 * (e.g. client/notebook/structured-tables/columns/ColumnRowMetadataUtil.ts)
 *
 * @param procedureId The id of the procedure to check if it is read only.
 * @param rowMetadata The row metadata for the cell.
 * @param columnDefinition The column definition for the cell.
 * @returns True if the procedure is read only for the cell, false otherwise.
 */
export function isProcedureReadOnlyForCell(
  procedureId: string,
  rowMetadata: MaybeProcedureColumnRowMetadata,
  columnDefinition: Optional<ColumnDefinition>
): boolean {
  // eslint-disable-next-line benchlint/no-in-operator
  if (!columnDefinition || !rowMetadata || !('procedureColumns' in rowMetadata)) {
    return false;
  }
  const columnGroup = columnDefinition.columnGroup ?? null;
  const columnType = columnDefinition.type;
  return rowMetadata.procedureColumns.some(
    (d) =>
      columnType === TableColumnType.PROCEDURE &&
      d.columnGroup === columnGroup &&
      d.readOnlyIds.includes(procedureId)
  );
}

export function toProcedureSearchItemsForCell(
  typedValue: TypedValue | null | undefined,
  rowMetadata: MaybeProcedureColumnRowMetadata,
  columnDefinition: Optional<ColumnDefinition>
): Array<ProcedureSearchItem> {
  const procedureSearchResponses = toProcedureSearchResponseFromTypedValue(typedValue);
  return procedureSearchResponses.map((procedureSearchResponse) => ({
    id: procedureSearchResponse.id,
    name: procedureSearchResponse.name,
    // Add isReadOnly flag based on the column definition and row metadata.
    isReadOnly: isProcedureReadOnlyForCell(procedureSearchResponse.id, rowMetadata, columnDefinition),
  }));
}
